import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, ArrowRight } from 'lucide-react';
import { CHURCH as CHURCH_DEFAULTS } from '../mock/mock';
import { subscribe } from '../lib/api';
import { useSettings } from '../hooks/useSettings';
import { useToast } from '../hooks/use-toast';
import Logo from './Logo';

const NAV = [
  { label: 'About', path: '/about' },
  { label: 'Connect', path: '/connect' },
  { label: 'Sermons', path: '/sermons' },
  { label: 'Events', path: '/events' },
  { label: 'Give', path: '/give' },
];

const Footer = () => {
  const settings = useSettings();
  const CHURCH = { ...CHURCH_DEFAULTS, ...settings };
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribe(email);
      toast({ title: 'You’re subscribed!', description: 'Thanks for joining the Living Hope family updates.' });
      setEmail('');
    } catch (err) {
      toast({ title: 'Subscribed!', description: 'Thanks for joining our updates.' });
      setEmail('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#0b2027] border-t border-white/10 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-3 gap-12 mb-16">
          <div>
            <Logo light={true} />
            <p className="text-white/60 mt-6 leading-relaxed max-w-xs">
              A family of believers who value relationship with God and one another. Hope is for everyone — and it is found in Jesus.
            </p>
            <div className="flex gap-4 mt-8">
              {[
                { Icon: Facebook, label: 'Facebook', href: CHURCH.facebookUrl || '#' },
                { Icon: Instagram, label: 'Instagram', href: '#' },
                { Icon: Youtube, label: 'YouTube', href: '#' },
              ].map(({ Icon, label, href }) => (
                <a key={label} href={href} target={href !== '#' ? '_blank' : undefined} rel={href !== '#' ? 'noopener noreferrer' : undefined} aria-label={label} className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:border-[#c9a24b] hover:text-[#c9a24b] transition-colors duration-300">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white uppercase tracking-[0.24em] text-xs mb-6">Explore</h4>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.path}>
                  <Link to={n.path} className="text-white/60 hover:text-[#c9a24b] transition-colors">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white uppercase tracking-[0.24em] text-xs mb-6">Stay Connected</h4>
            <p className="text-white/60 mb-5">Get church news and event updates in your inbox.</p>
            <form onSubmit={handleSubscribe} className="flex">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 bg-white/5 border border-white/20 text-white px-4 py-3 outline-none focus:border-[#c9a24b] transition-colors placeholder:text-white/40"
              />
              <button disabled={loading} className="bg-[#c9a24b] text-[#0b2027] px-4 flex items-center justify-center hover:bg-[#d8b566] transition-colors">
                <ArrowRight className="h-5 w-5" />
              </button>
            </form>
            <div className="mt-6 text-white/60 text-sm space-y-1">
              <p>{CHURCH.address}, {CHURCH.city}</p>
              <p>{CHURCH.phone}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">© {new Date().getFullYear()} {CHURCH.fullName}. All rights reserved.</p>
          <p className="text-white/40 text-sm font-serif-display italic">There is Hope. Jesus.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
